import { useEditor } from '@candulabs/craft';
import { CalloutIcon, Caption, css, styled } from 'canoe';
import { compose, connectWithHammock, ENDPOINTS } from 'hammock';
import React, { Fragment, useMemo } from 'react';

import { TabSection } from '@candu-ai/craft/ui';
import { EntitlementName, useEntitlementAccess } from '@candu-ai/organizations/entitlements';
import { useDefaultVariants } from '@candu-ai/styleguide/application-variants';
import { Flags, useFlags } from 'commons/hooks';
import { TemplateMetadata } from 'craft/components/TemplatesTab/types';
import { BuiltInComponents, CRAFT_ROOT_ID } from 'craft/constants';
import { useTraversal } from 'craft/hooks/useSelection/useTraversal';
import { useComponentsSearch } from 'craft/state';

import { allComponents } from './allComponents';
import { filterComponentsAgainstVariants } from './filterComponents';
import { processTemplateBlocks } from './processTemplateBlocks';

interface Props {
  templates: TemplateMetadata[];
}

const Grid = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    > * {
      margin-top: ${theme.spaces.xs};
      margin-right: ${theme.spaces.xxs};
    }
  `}
`;

const InfoBox = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.GlitterGrayL10};
    padding: ${theme.spaces.xs};
    display: flex;
    align-items: center;
    gap: ${theme.spaces.xs};
    margin-top: ${theme.spaces.xs};
  `}
`;

const FilteredComponentsImpl = ({ templates }: Props) => {
  const search = useComponentsSearch();
  const { data } = useDefaultVariants();
  const { variants: styleguideVariants } = data || {};
  const traversal = useTraversal();
  const { query } = useEditor();

  const { [Flags.FORMS]: withForms, [Flags.CONDITIONAL_VIEWS]: withConditionalViews } = useFlags();

  const [
    { hasEntitlementAccess: hasChecklistAccess },
    { hasEntitlementAccess: hasEmbedsAccess },
    { hasEntitlementAccess: hasFormsAccess },
  ] = useEntitlementAccess([
    EntitlementName.Checklist,
    EntitlementName.Embeds,
    EntitlementName.Forms,
  ]);

  const allNodes = useMemo(() => traversal.descendants(CRAFT_ROOT_ID), []);

  const unfilteredComponents = useMemo(() => {
    return [...allComponents, ...processTemplateBlocks(templates).reverse()];
  }, [allComponents, templates]);

  const componentsToRender = useMemo(
    () =>
      filterComponentsAgainstVariants(
        search ? search.toLowerCase().trim() : undefined,
        styleguideVariants,
        unfilteredComponents,
      ),
    [search, unfilteredComponents, styleguideVariants],
  );

  if (componentsToRender.length === 0) {
    return (
      <Caption mt="s" textAlign="center">
        Nothing found
      </Caption>
    );
  }

  const contentHasFormContainer = !!allNodes.find(
    (nId) => query.node(nId).get().data.name === BuiltInComponents.Form,
  );

  return (
    <>
      {componentsToRender
        .filter(
          ({ label }) =>
            withForms || hasFormsAccess || (label !== 'Form Container' && label !== 'Form Fields'),
        )
        .map(
          (
            { label, anchor, styleguideSection, styleguide, ComponentContainer, builtIn, variants },
            i,
          ) => {
            const filteredStyleguide =
              // only show Checklist dragIn if there is access to Checklist entitlement
              hasChecklistAccess || styleguide?.section !== 'checklist' ? styleguide : undefined;

            const filteredBuiltIn = (builtIn || [])
              .filter(
                ({ label: builtInLabel }) =>
                  // only show Content embed dragIn if there is access to Embeds entitlement
                  label !== 'Embeds' || hasEmbedsAccess || builtInLabel !== 'Content',
              )
              .filter(
                ({ label: builtInLabel }) =>
                  withConditionalViews || builtInLabel !== 'ConditionalView',
              );

            const StyleguideDropInComponent = filteredStyleguide?.Component || Fragment;
            const Container = ComponentContainer || Grid;
            return (
              <TabSection
                label={label}
                styleguideSection={styleguideSection}
                anchor={anchor}
                key={i.toString()}
              >
                {label === 'Form Fields' && !contentHasFormContainer && (
                  <InfoBox>
                    <CalloutIcon size="s" color="OnyxGrayL30" />
                    <Caption m="none">
                      Before using Form Fields, add a Form Container to your content.
                    </Caption>
                  </InfoBox>
                )}
                <Container>
                  {(variants || []).map((variant) => (
                    <StyleguideDropInComponent key={variant.name} variant={variant} />
                  ))}
                  {filteredBuiltIn.map(({ label: builtInLabel, Component }) => (
                    <Component
                      key={builtInLabel}
                      disabled={label === 'Form Fields' && !contentHasFormContainer}
                    />
                  ))}
                </Container>
              </TabSection>
            );
          },
        )}
    </>
  );
};

const withBlocks = connectWithHammock({
  endpoint: ENDPOINTS.templateMetadata,
  resourceName: 'templates',
  query: { finder: 'byEnabledBlock' },
});

export const FilteredComponents = compose(withBlocks)(FilteredComponentsImpl);
